/* -----------------Helpers & Hooks--------------- */
import { useExamContext } from "@features/exam/contexts/ExamContext";
/* -----------------UI--------------- */
import Box from "@memorang/ui/components/Box";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import { router } from "expo-router";

/* -----------------Types--------------- */
import type { PracticeTestExam } from "@memorang/types";

/* -----------------Child components--------------- */
import { PracticeTestsView } from "../components/PracticeTestsView";
import { NestedExamChipsContainer } from "../containers/NestedExamChipsContainer";

import { appBarMeta } from "@constants/appbar";
/* -----------------Helpers--------------- */
import { getPracticeTestsData } from "../helpers/practice-tests-data";

import CollectUserDetailsDialog from "@features/account/components/dialogs/CollectUserDetailsDialog";
import { useAppStore } from "@hooks/useAppStore";
import { useCanBypassPaywall } from "@hooks/useCanBypassPaywall";
import { isWeb } from "@memorang/helpers/platform";
import { ConfirmDialog } from "@memorang/ui/components/ConfirmDialog";
import { ErrorDialog } from "@memorang/ui/components/ErrorDialog";
import { OLXExamEligibilityDialog } from "@memorang/ui/components/OLXExamEligibilityDialog";
import { PracticeTestsSkeleton } from "@memorang/ui/components/PracticeTestListItem/PracticeTestsSkeleton";
import { useDevice } from "@memorang/ui/useDevice";
import { resumeSessionInfoDialogConfig } from "../constants";
import { usePracticeTests } from "../hooks/usePracticeTests";
import { AppBar } from "../layouts/AppBar";

const ResumeSessionInfoDialog = ({
	showResumeSessionInfoDialog,
	setShowResumeSessionInfoDialog,
	exam,
	creatingSession,
	handleActiveSession,
}: {
	showResumeSessionInfoDialog: boolean;
	setShowResumeSessionInfoDialog: (show: boolean) => void;
	exam: PracticeTestExam;
	creatingSession?: boolean;
	handleActiveSession: (examId: string, numericId?: number) => void;
}) => {
	const confirmDialogConfig = resumeSessionInfoDialogConfig({
		examName: exam.title,
		totalTime: exam.totalTime,
		timeElapsed: exam.timeElapsed,
	});
	return (
		<ConfirmDialog
			visible={showResumeSessionInfoDialog}
			dismissCallback={() => setShowResumeSessionInfoDialog(false)}
			title={confirmDialogConfig.title}
			body={confirmDialogConfig.body}
			icon="play-pause"
			confirmCallback={() => handleActiveSession(exam.id, exam.numericId)}
			confirmCtaLoading={creatingSession}
			confirmCta="Resume"
			dismissCta="Cancel"
		/>
	);
};

const PracticeTestsContainer = () => {
	const {
		currentExamContext: { examDate, examName },
	} = useExamContext();

	const {
		handlePracticeTestSession,
		selectedExamId,
		creatingSession,
		showErrorDialog,
		setShowErrorDialog,
		showCollectUserDetailsDialog,
		toggleShowCollectUserDetailsDialog,
		olxError,
		setOlxError,
		selectedNumericId,
		showResumeSessionInfoDialog,
		setShowResumeSessionInfoDialog,
		handleActiveSession,
		practiceTestDistribution,
		loading,
	} = usePracticeTests();

	const canBypassPaywall = useCanBypassPaywall();

	const practiceTestLabel = useAppStore(
		(state) => state.exam.practiceTest?.label,
	);
	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);

	const finalHeaderTitle =
		appBarMeta["practice-tests"].title(practiceTestLabel);

	const { isMobile } = useDevice();

	const showSkeleton = loading || !practiceTestDistribution;

	const practiceTestsData = practiceTestDistribution
		? getPracticeTestsData(
				practiceTestDistribution,
				examName,
				examDate,
				diagnosticLabel,
			)
		: undefined;

	const Wrapper = isWeb ? ScrollContainer : Box;

	const selectedExamForResume = selectedNumericId
		? practiceTestDistribution?.exams?.find(
				(exam) => exam.numericId === selectedNumericId,
			)
		: undefined;

	return (
		<>
			<AppBar title={finalHeaderTitle} />
			<Box position="relative" flexGrow={1} height="100%">
				<Wrapper
					maxWidth="sm"
					contentContainerStyle={{
						paddingHorizontal: isMobile ? 0 : 16,
						paddingTop: 16,
						paddingBottom: 96,
					}}
					// biome-ignore lint/a11y/useSemanticElements: <explanation>
					role="main"
				>
					<NestedExamChipsContainer
						disabled={showSkeleton}
						containerStyle={{
							paddingVertical: 24,
							paddingHorizontal: isMobile ? 0 : 16,
						}}
					/>
					{showSkeleton || !practiceTestsData ? (
						<PracticeTestsSkeleton />
					) : (
						<PracticeTestsView
							sections={practiceTestsData.sections}
							handleNavigation={handlePracticeTestSession}
							selectedExamId={
								showResumeSessionInfoDialog ? undefined : selectedExamId
							}
							disableCard={creatingSession}
							canBypassPaywall={canBypassPaywall}
							hasProductAccess={practiceTestsData.hasAccess}
						/>
					)}
					{showErrorDialog && (
						<ErrorDialog
							visible={showErrorDialog}
							handleClose={() => setShowErrorDialog(false)}
							title="Error"
							message="Session creation failed. Please try again."
						/>
					)}
					{!!olxError && (
						<OLXExamEligibilityDialog
							title={olxError.title}
							message={olxError.message}
							visible={!!olxError}
							handleClose={() => setOlxError(null)}
							onNavigateToPracticeTests={() => router.push("/practice-tests")}
						/>
					)}

					{showCollectUserDetailsDialog && (
						<CollectUserDetailsDialog
							visible={showCollectUserDetailsDialog}
							handleClose={toggleShowCollectUserDetailsDialog}
						/>
					)}
					{showResumeSessionInfoDialog && selectedExamForResume && (
						<ResumeSessionInfoDialog
							showResumeSessionInfoDialog={showResumeSessionInfoDialog}
							setShowResumeSessionInfoDialog={setShowResumeSessionInfoDialog}
							exam={selectedExamForResume}
							creatingSession={creatingSession}
							handleActiveSession={handleActiveSession}
						/>
					)}
				</Wrapper>
			</Box>
		</>
	);
};

export default PracticeTestsContainer;
